import React, { useEffect, useRef } from 'react';
import { Outlet, useLoaderData } from 'react-router';
import { Box, Typography, Paper, Button } from '@mui/material';
import { useNotifications } from '@toolpad/core/useNotifications';
import { cleanupGigsListener } from '../../store/slices/activeEventSlice';
import { AppDispatch, useDispatch, useSelector } from '../../store';
import { RootState } from '../../../shared/types/appTypes';
import { notifyError } from '../../utils/notifyError';

const SingleEventLayout: React.FC = () => {
  const { eventData } = useLoaderData() as { eventData: Promise<any> };
  const dispatch = useDispatch();
  const notifications = useNotifications();

  const activeEvent = useSelector((state: RootState) => state.activeEvent);
  const { error } = activeEvent;

  // Surface loader rejections via snackbar (instead of swallowing into console).
  useEffect(() => {
    Promise.resolve(eventData).catch((err) => {
      notifyError(notifications, err, 'Failed to load this event.');
    });

    return () => {
      dispatch(cleanupGigsListener());
    };
  }, [dispatch, eventData, notifications]);

  // Surface listener errors as they appear in Redux. Dedupe so the same
  // error message is not snackbarred twice in a row.
  const lastNotifiedErrorRef = useRef<string | null>(null);
  useEffect(() => {
    if (error && error !== lastNotifiedErrorRef.current) {
      lastNotifiedErrorRef.current = error;
      notifyError(notifications, error, 'An error occurred while loading event data.');
    }
    if (!error) {
      lastNotifiedErrorRef.current = null;
    }
  }, [error, notifications]);

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" color="error" gutterBottom>
            Error loading event
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {error}
          </Typography>
          <Button variant="outlined" onClick={() => window.location.reload()}>
            Reload page
          </Button>
        </Paper>
      </Box>
    );
  }

  return <Outlet />;
};

export default SingleEventLayout;
